import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import Navbar from "@/components/Navbar";
import Home from "@/pages/Home";
import Listings from "@/pages/Listings";
import PropertyDetail from "@/pages/PropertyDetail";
import AddProperty from "@/pages/AddProperty";
import MyListings from "@/pages/MyListings";
import MyEnquiries from "@/pages/MyEnquiries";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import About from "@/pages/About";
import AdminDashboard from "@/pages/AdminDashboard";
import Favorites from "@/pages/Favorites";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import NotFound from "@/pages/NotFound";
import KycVerify from "@/pages/KycVerify";
import Plans from "@/pages/Plans";
import Profile from "@/pages/Profile";
import InstallPrompt from "@/components/InstallPrompt";
import { useLang } from "@/context/LanguageContext";

function Shell() {
  const location = useLocation();
  // Handle Google OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<Listings />} />
          <Route path="/property/:id" element={<PropertyDetail />} />
          <Route path="/sell" element={<AddProperty />} />
          <Route path="/edit-property/:id" element={<AddProperty />} />
          <Route path="/my-listings" element={<MyListings />} />
          <Route path="/my-enquiries" element={<MyEnquiries />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/kyc" element={<KycVerify />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/cancellation" element={<Refund />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <InstallPrompt />
    </div>
  );
}

function Footer() {
  const { t } = useLang();
  return (
    <footer className="border-t border-border bg-accent/40 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-6">
          <div>
            <div className="wordmark text-3xl text-primary">
              Bhoomi<span className="wordmark-dot"></span><span className="wordmark-mitra">Mitra</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Bharat's trusted property marketplace — Ghar, Plot, Shop, Jamin.
            </p>
          </div>
          <div>
            <div className="overline text-secondary mb-2">Contact</div>
            <div className="text-sm space-y-1">
              <a href="tel:+917014965172" className="text-primary hover:underline flex items-center gap-1 font-medium" data-testid="footer-phone">
                📞 +91 70149 65172
              </a>
              <a href="https://wa.me/917014965172" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary text-xs">
                WhatsApp us
              </a>
            </div>
          </div>
          <div>
            <div className="overline text-secondary mb-2">Quick Links</div>
            <ul className="text-sm space-y-1">
              <li><a href="/browse" className="hover:text-primary">Browse Properties</a></li>
              <li><a href="/sell" className="hover:text-primary">Sell Property</a></li>
              <li><a href="/about" className="hover:text-primary">About Us</a></li>
              <li><a href="/terms" className="hover:text-primary" data-testid="footer-terms">Terms &amp; Conditions</a></li>
              <li><a href="/privacy" className="hover:text-primary" data-testid="footer-privacy">Privacy Policy</a></li>
              <li><a href="/refund" className="hover:text-primary" data-testid="footer-refund">Refund &amp; Cancellation</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} {t.brand} — All rights reserved.</div>
          <div className="text-center sm:text-right">{t.footer}</div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <ConfigProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <BrowserRouter>
                <Shell />
                <Toaster position="top-right" richColors closeButton />
              </BrowserRouter>
            </SubscriptionProvider>
          </AuthProvider>
        </ConfigProvider>
      </LanguageProvider>
    </div>
  );
}
